import React, { memo, useEffect, useState } from "react";
import BookingApi from "../../../API/Apis/BookingApi";
import { t } from "i18next";

import {
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Pagination,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import AppStandardButton from "../../UI/AppButtons";
import TextField from "@mui/material/TextField";
import DefaultPage from "../DefaultPage";
import { createTheme, Theme } from "@mui/material/styles";
import { formatThemeForReservedArea } from "../../../config/MuiColorThemes";
import i18n from "../../../config/i18n";
import CircularProgress from "@mui/material/CircularProgress";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

const formatDateShort = (dateStr: string) => {
  const d = new Date(dateStr);
  const dd = String(d.getUTCDate()).padStart(2, "0");
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const yy = String(d.getUTCFullYear()).slice(-2);
  return `${dd}/${mm}/${yy}`;
};

const formatTime = (dateStr: string) => dateStr.slice(11, 16);

function ReservationCard(props: any) {
  const { searchBooking } = props;
  const [allReservationList, setAllReservationList] = useState<any[]>([]);

  const [selectedFilter, setSelectedFilter] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [loader, setLoader] = useState(true);
  const rowsPerPage = 7;
  const [page, setPage] = useState(1);
  const [openModal, setOpenModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const [bookingCancelReason, setBookingCancelReason] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [prefix, setPrefix] = useState("");
  const [codice, setCodice] = useState("");
  const [number, setNumber] = useState("");
  const [reservationSourceCode, setReservationSourceCode] = useState("");
  const isSmallScreen = window.innerWidth <= 768;

  const [dynamicTheme, setDynamicTheme] = useState<Theme | null>(null);

  useEffect(() => {
    async function fetchTheme() {
      const dynamicPalette = await formatThemeForReservedArea();
      const theme = createTheme(dynamicPalette);
      setDynamicTheme(theme);
    }
    fetchTheme();
  }, []);

  const primaryColor = dynamicTheme?.palette["primary"]?.main;
  const lang = i18n.language;

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  useEffect(() => {
    setLoader(true);
    const id: any = localStorage.getItem("userID");
    BookingApi.getBookingDetailsbyUserId(id, 0, 10000).then((e) => {
      setAllReservationList(e.data.reservationList || []);
      setLoader(false);
    });
  }, [refreshKey]);

  useEffect(() => {
    setPage(1);
  }, [selectedFilter, searchBooking]);

  const handleBookingCancelReason = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setBookingCancelReason(event.target.value);
  };

  const cancelBooking = (
    prefix: string,
    number: string,
    codice: string,
    chanel: string
  ) => {
    setPrefix(prefix);
    setCodice(codice);
    setNumber(number);
    setReservationSourceCode(chanel);
    setModalMessage("Please provide the reason for canceling your booking");
    setMessageType("CancelBooking");
    setOpenModal(true);
  };

  const requestBookingCancel = () => {
    if (bookingCancelReason === "") {
      setErrorMessage("** Reason is mandatory **");
    } else {
      setMessageType("");
      setModalMessage("");
      setOpenModal(false);
      BookingApi.cancelBooking(
        prefix,
        number,
        codice,
        bookingCancelReason,
        reservationSourceCode
      )
        .then((e) => {
          const status = e.data.CancelStatus;
          if (status === "Cancelled") {
            setModalMessage("Booking Cancelled");
            setOpenModal(true);
            setRefreshKey((k) => k + 1);
          } else {
            setModalMessage("Booking not canceled");
            setOpenModal(true);
          }
        })
        .catch((e) => {
          console.log(e);
        });
    }
  };

  const onRequestClose = () => {
    setMessageType("");
    setModalMessage("");
    setErrorMessage("");
    setOpenModal(false);
    setBookingCancelReason("");
  };

  const toDateStr = (dateStr: string) => dateStr.slice(0, 10);
  const todayStr = new Date().toISOString().slice(0, 10);

  const applyFilters = (base: any[], search: string, filter: string | null) => {
    let list = base;
    if (filter === "future") {
      list = list.filter((r: any) => toDateStr(r.startDate) > todayStr);
    } else if (filter === "ongoing") {
      list = list.filter(
        (r: any) =>
          toDateStr(r.startDate) <= todayStr &&
          toDateStr(r.endDate) >= todayStr &&
          r.isCancel !== true
      );
    } else if (filter === "closed") {
      list = list.filter(
        (r: any) => toDateStr(r.endDate) < todayStr || r.isCancel === true
      );
    }
    if (search.trim()) {
      const searchText = search.trim().toLowerCase();
      list = list.filter((res: any) => {
        const p = res?.prefix?.trim().toLowerCase();
        const n = res?.number?.toString();
        const prefixAndnumber = `${p}${n}||${p} ${n}`.trim();
        const rawName = res?.clientiByIdCliente?.description || "";
        const clientName = rawName
          .split("/")[0]
          .split("-")
          .reverse()
          .join(" ")
          .toLowerCase();
        const email = (res?.clientiByIdCliente?.email || "").toLowerCase();
        return (
          p.includes(searchText) ||
          n.includes(searchText) ||
          prefixAndnumber.includes(searchText) ||
          clientName.includes(searchText) ||
          email.includes(searchText)
        );
      });
    }
    return list;
  };

  const handleDownload = (bookingId: string) => {
    BookingApi.getResPrint(bookingId || "", lang).then((e) => {
      const url = e?.file;
      const status = String(e?.data?.status);
      if (status === "error") {
        console.error("Download URL not available");
        return;
      }
      const linkSource = `data:application/pdf;base64,${url}`;
      const downloadLink = document.createElement("a");
      downloadLink.href = linkSource;
      downloadLink.download = "file.pdf";
      downloadLink.click();
    });
  };

  const futureCount = allReservationList.filter(
    (r: any) => toDateStr(r.startDate) > todayStr
  ).length;
  const onGoingCount = allReservationList.filter(
    (r: any) =>
      toDateStr(r.startDate) <= todayStr &&
      toDateStr(r.endDate) >= todayStr &&
      r.isCancel !== true
  ).length;
  const closedCount = allReservationList.filter(
    (r: any) => toDateStr(r.endDate) < todayStr || r.isCancel === true
  ).length;

  const statCards = [
    { label: t("Total"), count: allReservationList.length, key: null },
    { label: t("Future"), count: futureCount, key: "future" },
    { label: t("On going"), count: onGoingCount, key: "ongoing" },
    { label: t("Closed"), count: closedCount, key: "closed" },
  ];

  const filteredList = applyFilters(
    allReservationList,
    searchBooking,
    selectedFilter
  );
  const reservationList = filteredList.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const StatusBadge = ({ isConfirmed, isCancel }: any) => {
    if (isConfirmed === true) {
      return (
        <span
          style={{
            backgroundColor: "#E8FFF3",
            color: "#008741",
            borderRadius: "6px",
            padding: "4px 10px",
            fontSize: "12px",
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
        >
          {t("Confirmed")}
        </span>
      );
    }
    if (isConfirmed === false && isCancel === false) {
      return (
        <span
          style={{
            backgroundColor: "#FFF8E1",
            color: "#B7791F",
            borderRadius: "6px",
            padding: "4px 10px",
            fontSize: "12px",
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
        >
          {t("Pending")}
        </span>
      );
    }
    return (
      <span
        style={{
          backgroundColor: "#FFE8EA",
          color: "#C0392B",
          borderRadius: "6px",
          padding: "4px 10px",
          fontSize: "12px",
          fontWeight: 600,
          whiteSpace: "nowrap",
        }}
      >
        {t("Refused")}
      </span>
    );
  };

  return (
    <>
      {/* Cancel Booking Dialog */}
      <Dialog
        open={openModal}
        onClose={onRequestClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{ style: { borderRadius: "20px" } }}
      >
        <DialogTitle>{modalMessage}</DialogTitle>
        <DialogActions>
          <Grid container item xs={12}>
            {messageType === "CancelBooking" && (
              <Grid item xs={12}>
                <TextField
                  multiline
                  rows={5}
                  variant="outlined"
                  fullWidth
                  style={{ padding: "1rem" }}
                  onChange={handleBookingCancelReason}
                  value={bookingCancelReason}
                />
                <span
                  style={{
                    textAlign: "center",
                    color: "red",
                    display: "block",
                    fontStyle: "italic",
                  }}
                >
                  {errorMessage}
                </span>
              </Grid>
            )}
            <Grid item xs={12}>
              <Grid container justifyContent="center">
                {messageType === "CancelBooking" && (
                  <Grid item style={{ marginRight: "15px" }}>
                    <AppStandardButton
                      variant="contained"
                      onClick={requestBookingCancel}
                    >
                      <p className="text-light normal-case">Cancel Booking</p>
                    </AppStandardButton>
                  </Grid>
                )}
                <Grid item>
                  <AppStandardButton
                    variant="outlined"
                    onClick={onRequestClose}
                  >
                    <p className="text-black normal-case">Close</p>
                  </AppStandardButton>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>

      {/* Stat Cards */}
      {!loader && (
        <div className="flex gap-3 mb-5 flex-wrap">
          {statCards.map((s) => {
            const isActive = selectedFilter === s.key;
            return (
              <div
                key={s.label}
                className="flex-1 min-w-[100px] max-w-[160px] px-4 py-3"
                onClick={() => setSelectedFilter(s.key)}
                style={{
                  backgroundColor: isActive
                    ? `${primaryColor || "#2cb133"}12`
                    : "#f8f9fa",
                  border: isActive
                    ? `1.5px solid ${primaryColor || "#2cb133"}`
                    : "1px solid #e5e7eb",
                  borderRadius: "6px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  cursor: "pointer",
                  transition: "border 0.15s, background 0.15s",
                }}
              >
                <div className="flex items-center justify-between mb-1">
                  <span
                    style={{
                      fontSize: "13px",
                      color: isActive ? primaryColor || "#2cb133" : "#9ca3af",
                      fontWeight: isActive ? 600 : 400,
                    }}
                  >
                    {s.label}
                  </span>
                  <span
                    style={{
                      backgroundColor: isActive
                        ? primaryColor || "#2cb133"
                        : "#d1d5db",
                      color: "#fff",
                      fontSize: "11px",
                      fontWeight: 700,
                      borderRadius: "999px",
                      padding: "1px 8px",
                    }}
                  >
                    {s.count}
                  </span>
                </div>
                <div
                  style={{ fontSize: "26px", fontWeight: 700, color: "#111" }}
                >
                  {s.count}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Desktop Table */}
      {!isSmallScreen &&
        (loader ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "40px 0",
            }}
          >
            <CircularProgress style={{ color: primaryColor }} />
          </div>
        ) : (
          <div className="w-[100%] rounded-sm">
            {reservationList.length === 0 ? (
              <DefaultPage
                message={t(
                  "You have not created any booking yet. To create a new booking, please click on the Book a car button."
                )}
              />
            ) : (
              <>
                <TableContainer
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: "6px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                    overflow: "hidden",
                  }}
                >
                  <Table
                    aria-label="reservations table"
                    stickyHeader
                    style={{ borderCollapse: "collapse" }}
                  >
                    <TableHead>
                      <TableRow>
                        {[
                          t("Customer"),
                          t("Pick-up"),
                          t("Drop-off"),
                          t("Group"),
                          t("Totale"),
                          t("Voucher"),
                          t("Status"),
                          "",
                        ].map((col, idx) => (
                          <TableCell
                            key={idx}
                            style={{
                              backgroundColor: "#f8f9fa",
                              color: "#9ca3af",
                              fontSize: "12px",
                              fontWeight: 600,
                              borderBottom: "1px solid #e5e7eb",
                              letterSpacing: "0.03em",
                              padding: "10px 14px",
                              textAlign: idx === 0 ? "start" : "center",
                            }}
                          >
                            {col}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reservationList.map((e: any, i) => (
                        <TableRow
                          key={i}
                          sx={{
                            borderBottom: "1px solid #e5e7eb",
                            "&:hover": { backgroundColor: "#f3f4f6" },
                            "&:last-child td": { border: 0 },
                          }}
                        >
                          {/* Customer */}
                          <TableCell
                            style={{
                              fontSize: "13px",
                              padding: "12px 14px",
                              textAlign: "start",
                            }}
                          >
                            <div style={{ color: "#111", fontWeight: 500 }}>
                              {e.clientiByIdCliente.description
                                .split("/")[0]
                                .split("-")
                                .reverse()
                                .join(" ") || ""}
                            </div>
                            <div
                              style={{
                                color: "#9ca3af",
                                fontSize: "11px",
                                marginTop: "2px",
                              }}
                            >
                              {e.clientiByIdCliente.email || ""}
                            </div>
                          </TableCell>

                          {/* Pick-up */}
                          <TableCell
                            style={{
                              fontSize: "13px",
                              padding: "12px 14px",
                              textAlign: "center",
                            }}
                          >
                            <div style={{ color: "#333", fontWeight: 500 }}>
                              {e.locationPickupExcepted.description.split(
                                "-"
                              )[0] || ""}
                            </div>
                            <div style={{ color: "#9ca3af", fontSize: "11px" }}>
                              {formatDateShort(e.startDate)}
                            </div>
                            <div style={{ color: "#9ca3af", fontSize: "11px" }}>
                              {formatTime(e.startDate)}
                            </div>
                          </TableCell>

                          {/* Drop-off */}
                          <TableCell
                            style={{
                              fontSize: "13px",
                              padding: "12px 14px",
                              textAlign: "center",
                            }}
                          >
                            <div style={{ color: "#333", fontWeight: 500 }}>
                              {e.locationReturnExcepted.description.split(
                                "-"
                              )[0] || ""}
                            </div>
                            <div style={{ color: "#9ca3af", fontSize: "11px" }}>
                              {formatDateShort(e.endDate)}
                            </div>
                            <div style={{ color: "#9ca3af", fontSize: "11px" }}>
                              {formatTime(e.endDate)}
                            </div>
                          </TableCell>

                          {/* Group */}
                          <TableCell
                            style={{
                              fontSize: "13px",
                              color: "#333",
                              padding: "12px 14px",
                              textAlign: "center",
                            }}
                          >
                            {e.assignedGroup.description || ""}
                          </TableCell>

                          {/* Total */}
                          <TableCell
                            style={{
                              fontSize: "13px",
                              color: "#333",
                              fontWeight: 500,
                              padding: "12px 14px",
                              textAlign: "center",
                            }}
                          >
                            {"€ " + e.totalRental || ""}
                          </TableCell>

                          {/* Voucher */}
                          <TableCell
                            style={{
                              fontSize: "13px",
                              color: "#333",
                              padding: "12px 14px",
                              textAlign: "center",
                            }}
                          >
                            {`${e.prefix || ""} ${e.number || ""}`}
                          </TableCell>

                          {/* Status */}
                          <TableCell
                            style={{
                              padding: "12px 14px",
                              textAlign: "center",
                            }}
                          >
                            <StatusBadge
                              isConfirmed={e.isConfirmed}
                              isCancel={e.isCancel}
                            />
                          </TableCell>

                          {/* Actions */}
                          <TableCell style={{ padding: "12px 14px" }}>
                            <div className="flex items-center gap-1">
                              <button
                                disabled={e.isCancel === true}
                                title={t("Cancel")}
                                onClick={() =>
                                  cancelBooking(
                                    e.prefix,
                                    e.number,
                                    e.codice,
                                    e.reservationSourceCode
                                  )
                                }
                                style={{
                                  background: "none",
                                  border: "none",
                                  cursor:
                                    e.isCancel === true
                                      ? "not-allowed"
                                      : "pointer",
                                  color:
                                    e.isCancel === true ? "#e5e7eb" : "#9ca3af",
                                  padding: "4px",
                                  display: "flex",
                                  alignItems: "center",
                                  borderRadius: "6px",
                                  transition: "color 0.15s",
                                }}
                              >
                                <DeleteOutlineIcon style={{ fontSize: 18 }} />
                              </button>
                              <button
                                title={t("Download")}
                                onClick={() => handleDownload(e.id)}
                                style={{
                                  background: "none",
                                  border: "none",
                                  cursor: "pointer",
                                  color: "#9ca3af",
                                  padding: "4px",
                                  display: "flex",
                                  alignItems: "center",
                                  borderRadius: "6px",
                                  transition: "opacity 0.15s",
                                }}
                              >
                                <DownloadIcon style={{ fontSize: 18 }} />
                              </button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    padding: "16px 0",
                  }}
                >
                  <Pagination
                    count={Math.ceil(filteredList.length / rowsPerPage)}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    shape="rounded"
                    size="small"
                    sx={{
                      "& .MuiPaginationItem-root.Mui-selected": {
                        backgroundColor: alpha(primaryColor || "#2cb133", 0.1),
                        color: primaryColor || "#2cb133",
                        fontWeight: 600,
                        "&:hover": {
                          backgroundColor: alpha(
                            primaryColor || "#2cb133",
                            0.18
                          ),
                        },
                      },
                      "& .MuiPaginationItem-root": {
                        "&:hover": {
                          backgroundColor: alpha(
                            primaryColor || "#2cb133",
                            0.06
                          ),
                        },
                      },
                    }}
                  />
                </div>
              </>
            )}
          </div>
        ))}

      {/* Mobile Cards */}
      {isSmallScreen &&
        (loader ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "40px 0",
            }}
          >
            <CircularProgress style={{ color: primaryColor }} />
          </div>
        ) : (
          <div
            className="grid grid-cols-1 gap-4 sm:grid-cols-2"
            style={{ marginLeft: "-5px" }}
          >
            {reservationList.length === 0 ? (
              <DefaultPage
                message={t(
                  "You have not created any booking yet. To create a new booking, please click on the Book a car button."
                )}
              />
            ) : (
              <>
                <Grid container spacing={2}>
                  {reservationList.map((e: any, i) => (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      key={i}
                      style={{ paddingLeft: "0px" }}
                    >
                      <Card
                        className="!shadow-2xl rounded-2xl !border-solid !border-2 !border-grey-400 ml-5"
                        style={{ borderRadius: "10px" }}
                      >
                        <CardHeader
                          title={`${e.prefix || ""} ${e.number || ""}`}
                          style={{
                            color: "white",
                            backgroundColor:
                              dynamicTheme?.palette?.["primary"]?.main,
                            height: "40px",
                            borderRadius: "10px 10px 0 0",
                          }}
                          titleTypographyProps={{
                            style: {
                              color:
                                dynamicTheme?.palette?.["primary"]
                                  ?.contrastText,
                            },
                          }}
                        />
                        <CardContent>
                          {[
                            {
                              label: t("Customer"),
                              value: e.clientiByIdCliente.description
                                .split("/")[0]
                                .split("-")
                                .reverse()
                                .join(" "),
                            },
                            {
                              label: t("Email"),
                              value: e.clientiByIdCliente.email || "",
                            },
                            {
                              label: t("Pick-up"),
                              value: `${
                                e.locationPickupExcepted.description.split(
                                  "-"
                                )[0]
                              } ${formatDateShort(e.startDate)} ${formatTime(
                                e.startDate
                              )}`,
                            },
                            {
                              label: t("Drop-off"),
                              value: `${
                                e.locationReturnExcepted.description.split(
                                  "-"
                                )[0]
                              } ${formatDateShort(e.endDate)} ${formatTime(
                                e.endDate
                              )}`,
                            },
                            {
                              label: t("Group"),
                              value: e.assignedGroup.description || "",
                            },
                            {
                              label: t("Total"),
                              value: "€ " + e.totalRental,
                            },
                          ].map((row) => (
                            <React.Fragment key={row.label}>
                              <Typography
                                className="!text-[14px]"
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                }}
                              >
                                <span className="text-[#555]">{row.label}</span>
                                <span className="font-medium">{row.value}</span>
                              </Typography>
                              <hr />
                            </React.Fragment>
                          ))}
                          <div className="flex items-center justify-between mt-2">
                            <StatusBadge
                              isConfirmed={e.isConfirmed}
                              isCancel={e.isCancel}
                            />
                            <div className="flex gap-2 items-center">
                              <button
                                disabled={e.isCancel === true}
                                onClick={() =>
                                  cancelBooking(
                                    e.prefix,
                                    e.number,
                                    e.codice,
                                    e.reservationSourceCode
                                  )
                                }
                                style={{
                                  background: "none",
                                  border: "none",
                                  color: "#888",
                                  padding: "4px",
                                  display: "flex",
                                }}
                              >
                                <DeleteOutlineIcon style={{ fontSize: 20 }} />
                              </button>
                              <button
                                onClick={() => handleDownload(e.id)}
                                style={{
                                  background: "none",
                                  border: "none",
                                  cursor: "pointer",
                                  color: "#9ca3af",
                                  padding: "4px",
                                  display: "flex",
                                }}
                              >
                                <DownloadIcon style={{ fontSize: 20 }} />
                              </button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    padding: "16px 0",
                  }}
                >
                  <Pagination
                    count={Math.ceil(filteredList.length / rowsPerPage)}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    shape="rounded"
                    size="small"
                    sx={{
                      "& .MuiPaginationItem-root.Mui-selected": {
                        backgroundColor: alpha(primaryColor || "#2cb133", 0.1),
                        color: primaryColor || "#2cb133",
                        fontWeight: 600,
                        "&:hover": {
                          backgroundColor: alpha(
                            primaryColor || "#2cb133",
                            0.18
                          ),
                        },
                      },
                      "& .MuiPaginationItem-root": {
                        "&:hover": {
                          backgroundColor: alpha(
                            primaryColor || "#2cb133",
                            0.06
                          ),
                        },
                      },
                    }}
                  />
                </div>
              </>
            )}
          </div>
        ))}
    </>
  );
}

export default memo(ReservationCard);
